import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Briefcase, Plus, Users, Award, TrendingUp, MapPin, DollarSign, ChevronRight, CheckCircle, UserCheck, Mail, Send, X, Store } from '../components/CustomIcons';
import EmptyState from '../components/EmptyState';
import WelcomeModal from '../components/WelcomeModal';
import { useMarkOnboardingPath } from '../components/WelcomeModal';

export default function EmployerDashboard() {
  const { authFetch, user } = useAuth();
  const [listings, setListings] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [showPostJob, setShowPostJob] = useState(false);
  const [tab, setTab] = useState('listings');
  const [messages, setMessages] = useState([]);
  const [compose, setCompose] = useState(null); // { candidate_id, name, job_id, job_title }
  const [msgForm, setMsgForm] = useState({ subject: '', body: '' });
  const [msgSending, setMsgSending] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState(false);
  const [form, setForm] = useState({ title: '', location: '', wage_min: 15, wage_max: 22, job_type: 'Full-time', sector: 'Construction', description: '', second_chance: true, wotc_eligible: true });
  const [postSuccess, setPostSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ minProgress: 0, availableMonths: '' });

  // Demo Mode: lives in sessionStorage, resets on reload
  const [isDemoMode, setIsDemoMode] = useState(() => sessionStorage.getItem('aos_employer_demo_mode') === '1');
  const toggleDemo = () => {
    const next = !isDemoMode;
    sessionStorage.setItem('aos_employer_demo_mode', next ? '1' : '0');
    setIsDemoMode(next);
  };

  const profile = user?.profile || {};
  const profileIsIncomplete = !profile.company_name || !profile.industry || !profile.location;

  useMarkOnboardingPath('/jobs/employer');

  useEffect(() => {
    Promise.all([
      authFetch('/jobs/employer/listings'),
      authFetch(`/jobs/employer/candidates?min_progress=${filters.minProgress}${filters.availableMonths ? `&available_within_months=${filters.availableMonths}` : ''}`),
      authFetch('/employer/messages/sent'),
    ]).then(([listR, candR, msgR]) => {
      setListings(listR.data);
      setCandidates(candR.data);
      setMessages(msgR.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePostJob = async () => {
    try {
      const r = await authFetch('/jobs/employer/post', { method: 'POST', data: form });
      setListings(prev => [r.data, ...prev]);
      setPostSuccess(true);
      setShowPostJob(false);
      setTimeout(() => setPostSuccess(false), 3000);
    } catch (e) { console.error('post-job error:', e); }
  };

  const handleSendMessage = async () => {
    if (!compose || !msgForm.subject.trim() || !msgForm.body.trim()) return;
    setMsgSending(true);
    try {
      const r = await authFetch('/employer/messages', {
        method: 'POST',
        data: {
          candidate_id: compose.candidate_id,
          subject: msgForm.subject.trim(),
          body: msgForm.body.trim(),
          job_id: compose.job_id || '',
          job_title: compose.job_title || '',
        },
      });
      setMessages(prev => [r.data.message, ...prev]);
      setMsgSuccess(true);
      setTimeout(() => { setMsgSuccess(false); setCompose(null); setMsgForm({ subject: '', body: '' }); }, 2000);
    } catch (e) {
      console.error('send-message error:', e);
    } finally {
      setMsgSending(false);
    }
  };

  return (
    <div style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="employer-dashboard">
      {/* Demo Mode banner */}
      {isDemoMode && (
        <div data-testid="employer-demo-banner" style={{ background: '#F97316', color: '#0F2744', padding: '8px 24px', margin: '-24px -24px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px' }}>
          <span>Live Demo · AssistivOS — Sample data shown. Destructive actions disabled.</span>
          <button onClick={toggleDemo} data-testid="exit-demo-banner-btn" style={{ background: 'rgba(15,39,68,0.15)', border: 'none', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', color: '#0F2744', fontWeight: 700, fontSize: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Exit Demo</button>
        </div>
      )}
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
          <div>
            <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>
              {profile.company_name || 'Employer Dashboard'}
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '4px 0 0' }}>{profile.industry} · {profile.location}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {/* Demo Mode toggle */}
            <button
              onClick={toggleDemo}
              data-testid="employer-demo-toggle-btn"
              style={{ display: 'flex', alignItems: 'center', gap: '7px', background: isDemoMode ? '#F97316' : 'var(--bg-surface)', border: isDemoMode ? 'none' : '1px solid var(--border-subtle)', borderRadius: '10px', padding: '9px 14px', cursor: 'pointer', color: isDemoMode ? '#0F2744' : 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', transition: 'all 0.15s' }}>
              <Briefcase size={13} /> {isDemoMode ? 'Exit Demo' : 'Demo Mode'}
            </button>
            {/* Marketplace shortcut */}
            <Link to="/marketplace" data-testid="marketplace-shortcut-btn" style={{ display: 'flex', alignItems: 'center', gap: '7px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '9px 14px', cursor: 'pointer', color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', textDecoration: 'none' }}>
              <Store size={13} /> Talent Marketplace
            </Link>
            <button onClick={() => setShowPostJob(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#F97316', border: 'none', borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', color: '#111111', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px' }} data-testid="post-job-btn">
              <Plus size={16} /> Post a Job
            </button>
          </div>
        </div>

        {postSuccess && (
          <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CheckCircle size={16} color="#22C55E" />
            <span style={{ fontSize: '14px', color: '#22C55E', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>Job posted successfully!</span>
          </div>
        )}

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '24px' }}>
          {[
            { label: 'Active Listings', value: listings.length, icon: Briefcase },
            { label: 'Qualified Candidates', value: candidates.length, icon: Users },
            { label: 'Applications', value: listings.reduce((s, j) => s + (j.applicants || 0), 0), icon: TrendingUp },
            { label: 'WOTC Eligible', value: candidates.filter(c => (c.credentials || []).length >= 1).length, icon: Award },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '12px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{s.label}</span>
                <s.icon size={14} color="#F97316" />
              </div>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--text-primary)' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-surface)', borderRadius: '10px', padding: '4px', marginBottom: '20px', width: 'fit-content' }}>
          {['listings', 'candidates', 'messages'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: 'none', background: tab === t ? '#F97316' : 'transparent', color: tab === t ? '#111111' : '#666666', fontFamily: 'Plus Jakarta Sans, sans-serif', textTransform: 'capitalize' }}
              data-testid={`employer-tab-${t}`}>
              {t === 'messages' ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {t}
                  {messages.length > 0 && <span style={{ background: tab === 'messages' ? '#111' : '#F97316', color: tab === 'messages' ? '#fff' : '#111', borderRadius: '10px', padding: '1px 7px', fontSize: '10px', fontWeight: 700 }}>{messages.length}</span>}
                </span>
              ) : t}
            </button>
          ))}
        </div>

        {/* Listings */}
        {tab === 'listings' && (
          <div>
            {listings.length === 0 ? (
              <div>
                <EmptyState
                  icon={Briefcase}
                  title="No job listings yet"
                  subtitle="Post your first position to start attracting credential-verified, job-ready candidates."
                  action={{ label: 'Post a Job', onClick: () => setShowPostJob(true) }}
                  testId="employer-listings-empty"
                />
                {/* Profile completion prompt */}
                {profileIsIncomplete && (
                  <div style={{ background: 'var(--bg-surface)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '14px', padding: '20px', marginTop: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <UserCheck size={20} color="#F97316" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)', margin: '0 0 3px 0' }}>Complete your company profile</p>
                      <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>Adding your industry, location, and company name makes your listings more visible to qualified candidates.</p>
                    </div>
                    <button
                      onClick={() => window.location.href = '/jobs/employer'}
                      style={{ background: '#F97316', color: '#111111', border: 'none', borderRadius: '8px', padding: '8px 16px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px', cursor: 'pointer', flexShrink: 0, minHeight: '40px' }}
                      data-testid="complete-profile-btn"
                    >
                      Edit Profile
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {listings.map(job => (
                  <div key={job.id} style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px' }} data-testid={`job-listing-${job.id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div>
                        <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)' }}>{job.title}</div>
                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', gap: '16px', marginTop: '4px' }}>
                          <span><MapPin size={11} style={{ display: 'inline' }} /> {job.location}</span>
                          <span><DollarSign size={11} style={{ display: 'inline' }} /> ${job.wage_min}–${job.wage_max}/hr</span>
                        </div>
                      </div>
                      <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{job.type}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {job.second_chance && <span style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '5px' }}>SECOND CHANCE</span>}
                      {job.wotc_eligible && <span style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '5px' }}>WOTC ELIGIBLE</span>}
                      <span style={{ background: 'var(--bg-page)', color: 'var(--text-secondary)', fontSize: '11px', padding: '2px 8px', borderRadius: '5px' }}>{job.applicants || 0} applicants</span>
                    </div>
                  </div>
                ))}
                {/* Profile completion prompt when listings exist but profile incomplete */}
                {profileIsIncomplete && (
                  <div style={{ background: 'var(--bg-surface)', border: '1px dashed rgba(249,115,22,0.3)', borderRadius: '14px', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <UserCheck size={18} color="#F97316" />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 2px 0' }}>Profile incomplete</p>
                      <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>Add industry and location to improve candidate matching.</p>
                    </div>
                    <button onClick={() => window.location.href = '/jobs/employer'} style={{ background: 'transparent', border: '1px solid rgba(249,115,22,0.3)', color: '#F97316', borderRadius: '7px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', minHeight: '36px' }} data-testid="complete-profile-inline-btn">Complete</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Candidates */}
        {tab === 'candidates' && (
          <div data-testid="candidates-list">
            {/* Filters */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 600 }}>Filter:</span>
              <select
                value={filters.minProgress}
                onChange={e => setFilters(f => ({ ...f, minProgress: Number(e.target.value) }))}
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '7px 12px', color: 'var(--text-primary)', fontSize: '13px', cursor: 'pointer' }}
                data-testid="candidate-progress-filter"
              >
                <option value={0}>Any progress</option>
                <option value={25}>25%+ complete</option>
                <option value={50}>50%+ complete</option>
                <option value={75}>75%+ complete</option>
              </select>
              <select
                value={filters.availableMonths}
                onChange={e => setFilters(f => ({ ...f, availableMonths: e.target.value }))}
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '7px 12px', color: 'var(--text-primary)', fontSize: '13px', cursor: 'pointer' }}
                data-testid="candidate-availability-filter"
              >
                <option value="">Any availability</option>
                <option value="3">Available in 3 months</option>
                <option value="6">Available in 6 months</option>
                <option value="12">Available in 12 months</option>
              </select>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{candidates.length} candidate{candidates.length !== 1 ? 's' : ''} found</span>
            </div>

            {candidates.map((c, i) => (
              <div key={c.candidate_id || `cand-${i}`} style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px', marginBottom: '12px', display: 'flex', gap: '16px', alignItems: 'flex-start' }} data-testid={`candidate-card-${i}`}>
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: '#F97316', flexShrink: 0 }}>
                  {c.candidate_id?.split('-')[1]?.slice(0, 2) || 'C'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
                      Candidate {c.candidate_id}
                    </div>
                    {c.match_score >= 55 && (
                      <span style={{ fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '5px', background: 'rgba(34,197,94,0.12)', color: '#22C55E', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>STRONG MATCH</span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <span>{c.current_pathway ? c.current_pathway.replace(/_/g, ' ').replace(/\b\w/g, x => x.toUpperCase()) : 'No pathway'}</span>
                    {c.pathway_progress_pct > 0 && <span style={{ color: 'var(--text-secondary)' }}>{c.pathway_progress_pct}% done</span>}
                    {c.release_date && <span>Available {new Date(c.release_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>}
                    {c.streak_days > 0 && <span style={{ color: '#F97316' }}>{c.streak_days}d streak</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {(c.credentials || []).map(cred => (
                      <span key={cred.title} style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', color: '#22C55E', fontSize: '11px', padding: '2px 8px', borderRadius: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CheckCircle size={10} /> {cred.title}
                      </span>
                    ))}
                    {(c.credentials || []).length === 0 && <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>In training</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  {/* Match Score */}
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '50%', marginBottom: '6px',
                    background: `conic-gradient(${c.match_score >= 55 ? '#22C55E' : c.match_score >= 40 ? '#F97316' : '#444'} ${c.match_score * 3.6}deg, var(--bg-elevated) 0deg)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', marginLeft: 'auto'
                  }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '11px', fontWeight: 800, color: c.match_score >= 55 ? '#22C55E' : 'var(--text-primary)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{c.match_score}%</span>
                    </div>
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>match</div>
                  <button
                    data-testid={`message-candidate-btn-${i}`}
                    onClick={() => {
                      setCompose({ candidate_id: c.candidate_id, name: `Candidate ${c.candidate_id}`, job_id: '', job_title: '' });
                      setMsgForm({ subject: '', body: '' });
                      setTab('messages');
                    }}
                    style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '5px', background: 'transparent', border: '1px solid rgba(249,115,22,0.35)', borderRadius: '7px', padding: '6px 11px', cursor: 'pointer', color: '#F97316', fontSize: '11px', fontWeight: 600 }}>
                    <Mail size={11} /> Message
                  </button>
                </div>
              </div>
            ))}
            {candidates.length === 0 && (
              <EmptyState
                icon={Users}
                title="No candidates match current filters"
                subtitle="Try relaxing the progress or availability filters to see more job-ready candidates."
                action={{ label: 'Clear filters', onClick: () => setFilters({ minProgress: 0, availableMonths: '' }) }}
                testId="employer-candidates-empty"
              />
            )}
          </div>
        )}

        {/* Post Job Modal */}
        {showPostJob && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '32px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '20px', color: 'var(--text-primary)', margin: '0 0 24px' }}>Post a Job</h3>
              {[
                { label: 'Job Title', key: 'title', type: 'text', placeholder: 'e.g. Electrician Apprentice' },
                { label: 'Location', key: 'location', type: 'text', placeholder: 'e.g. Dallas, TX' },
              ].map(f => (
                <div key={f.key} style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '6px', letterSpacing: '0.5px' }}>{f.label.toUpperCase()}</label>
                  <input value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    style={{ width: '100%', background: 'var(--bg-page)', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '10px 14px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '6px', letterSpacing: '0.5px' }}>MIN WAGE ($/hr)</label>
                  <input type="number" value={form.wage_min} onChange={e => setForm(p => ({ ...p, wage_min: Number(e.target.value) }))}
                    style={{ width: '100%', background: 'var(--bg-page)', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '10px 14px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '6px', letterSpacing: '0.5px' }}>MAX WAGE ($/hr)</label>
                  <input type="number" value={form.wage_max} onChange={e => setForm(p => ({ ...p, wage_max: Number(e.target.value) }))}
                    style={{ width: '100%', background: 'var(--bg-page)', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '10px 14px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '6px', letterSpacing: '0.5px' }}>DESCRIPTION</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe the role, requirements, and benefits..."
                  rows={3}
                  style={{ width: '100%', background: 'var(--bg-page)', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '10px 14px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', resize: 'vertical', fontFamily: 'Inter, sans-serif', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                {[
                  { key: 'second_chance', label: 'Second Chance Employer' },
                  { key: 'wotc_eligible', label: 'WOTC Eligible' },
                ].map(opt => (
                  <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={form[opt.key]} onChange={e => setForm(p => ({ ...p, [opt.key]: e.target.checked }))}
                      style={{ accentColor: '#F97316' }} />
                    <span style={{ fontSize: '13px', color: '#AAAAAA' }}>{opt.label}</span>
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setShowPostJob(false)} style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-default)', borderRadius: '10px', padding: '11px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '14px' }}>Cancel</button>
                <button onClick={handlePostJob} style={{ flex: 2, background: '#F97316', border: 'none', borderRadius: '10px', padding: '11px', cursor: 'pointer', color: '#111111', fontWeight: 700, fontSize: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="post-job-submit-btn">Post Job</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Messages Tab */}
      {tab === 'messages' && (
        <div style={{ maxWidth: '700px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)' }}>
              Sent Messages ({messages.length})
            </span>
            <button
              data-testid="compose-message-btn"
              onClick={() => { setCompose({ candidate_id: '', name: 'a candidate' }); setMsgForm({ subject: '', body: '' }); }}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#F97316', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', color: '#111', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px' }}>
              <Send size={13} /> Compose
            </button>
          </div>
          {messages.length === 0 ? (
            <EmptyState icon={Mail} title="No messages sent yet" subtitle="Use the Candidates tab to find talent and reach out directly." action={{ label: 'Browse Candidates', onClick: () => setTab('candidates') }} testId="employer-messages-empty" />
          ) : (
            <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', overflow: 'hidden' }}>
              {messages.map((msg, idx) => (
                <div key={msg.id || idx} style={{ padding: '16px 20px', borderBottom: '1px solid #141414', display: 'flex', alignItems: 'flex-start', gap: '12px' }}
                  data-testid={`sent-message-${idx}`}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Mail size={15} color="#F97316" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {msg.subject}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '3px' }}>
                      To: {msg.recipient_name}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                      {msg.created_at ? new Date(msg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                      {msg.read ? <span style={{ marginLeft: '8px', color: '#22C55E' }}>Read</span> : <span style={{ marginLeft: '8px', color: '#F97316' }}>Unread</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Compose Message Modal */}
      {compose !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: '18px', padding: '28px', width: '100%', maxWidth: '500px', border: '1px solid #2A2A2A', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Mail size={18} color="#F97316" />
                <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '17px', color: 'var(--text-primary)', margin: 0 }}>
                  {compose.candidate_id ? `Message ${compose.name}` : 'Compose Message'}
                </h3>
              </div>
              <button onClick={() => setCompose(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px' }}><X size={18} /></button>
            </div>

            {!compose.candidate_id && (
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Recipient (Candidate ID)</label>
                <input className="input-dark" placeholder="user-marcus-001" value={compose.candidate_id}
                  onChange={e => setCompose(p => ({ ...p, candidate_id: e.target.value }))} />
              </div>
            )}

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Subject</label>
              <input className="input-dark" placeholder="Job opportunity: Electrician Helper" value={msgForm.subject}
                onChange={e => setMsgForm(p => ({ ...p, subject: e.target.value }))}
                data-testid="message-subject-input" />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Message</label>
              <textarea className="input-dark" rows={5} placeholder="Write your message here..."
                value={msgForm.body} onChange={e => setMsgForm(p => ({ ...p, body: e.target.value }))}
                data-testid="message-body-input"
                style={{ resize: 'vertical', minHeight: '100px', fontFamily: 'Inter, sans-serif' }} />
            </div>
            {msgSuccess && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px' }}>
                <CheckCircle size={15} color="#22C55E" />
                <span style={{ fontSize: '13px', color: '#22C55E', fontWeight: 600 }}>Message sent successfully!</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setCompose(null)} style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-default)', borderRadius: '10px', padding: '11px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '14px' }}>Cancel</button>
              <button onClick={handleSendMessage} disabled={!compose.candidate_id || !msgForm.subject.trim() || !msgForm.body.trim() || msgSending}
                data-testid="send-message-submit-btn"
                style={{ flex: 2, background: '#F97316', border: 'none', borderRadius: '10px', padding: '11px', cursor: 'pointer', color: '#111', fontWeight: 700, fontSize: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif', opacity: (!compose.candidate_id || !msgForm.subject.trim() || !msgForm.body.trim()) ? 0.5 : 1 }}>
                <Send size={14} style={{ display: 'inline', marginRight: '6px' }} />
                {msgSending ? 'Sending…' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}

      <WelcomeModal />
    </div>
  );
}
